import { createApi } from '@reduxjs/toolkit/query/react'
import { API_ENDPOINTS } from 'Constants'
import { INestedFormPopulate } from 'Models/BOB/ResponseModels/Life/QuestionSet'
import { GetFileDocumentResponse, TicketResponse } from 'Pages/Customer360/TrackRequest/types'
import { IPolicyItem, RaiseClaimResponse } from 'Pages/Customer360/types'
import {
  IRenewalProduct,
  IRenewalPolicyStatus,
  IApiEnvelope,
  IRenewalSearchResult,
  IStandaloneRenewalConsentRequest,
  IStandaloneRenewalConsentResponse,
  ISpConsentStatus,
  ISpConsentSendRequest,
  ISpConsentSendResponse,
  IStartRenewalSearchRequest,
  IStartRenewalSearchResponse,
} from 'Pages/Renewal/types'
import { IDynamicObject } from 'Pages/Sachet/BGR/General/Modals'
import { bobOperationUrlQuery } from 'Services/baseQuery'

interface IRaiseClaim {
  ticket_number: string
}

interface IdynamicObject {
  [key: string]: any
}

export interface IRaiseEndorsementRequest {
  pas_id: string
  insurance_category: string
  endorsement_type?: string
  endorsement_data?: IdynamicObject
  cancellation_data?: IdynamicObject
  grievance_data?: IdynamicObject
  surrender_data?: IdynamicObject
  changed_fields: IdynamicObject[]
  document_upload?: IdynamicObject
  remarks?: string
  consent_id?: string
  previous_endorsement_data?: IDynamicObject
  changes_in_remarks?: string
  journey_type?: string
  otherDocumentsRemarks?: string
  email?: string
  phone?: string
  pas_data?: any
}

export const customer360Api = createApi({
  reducerPath: 'policyDataQuery',
  baseQuery: bobOperationUrlQuery,
  endpoints: (builder) => ({
    getPolicyListing: builder.query<IPolicyItem[], Record<string, string | string[]>>({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_POLICY_LISTING}`,
        method: 'GET',
        params: filters,
        // rawResponse: true,
      }),
    }),
    getLifeEndorseFormFields: builder.query<
      {
        data: INestedFormPopulate
        message: string
        num_pages: number
        page: number
        status: number
      },
      Record<string, string>
    >({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_LIFE_ENDORSE_FINANCIAL}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),
    raiseClaim: builder.mutation<RaiseClaimResponse, Record<string, any>>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.RAISE_CLAIM,
        method: 'POST',
        body: body,
        rawResponse: true,
      }),
    }),

    getTicketListing: builder.query<TicketResponse, Record<string, string>>({
      query: (filters) => ({
        url: `${API_ENDPOINTS.BOB.GET_TICKET_LISTING}`,
        method: 'GET',
        params: filters,
        rawResponse: true,
      }),
    }),

    cancellationClaim: builder.mutation<RaiseClaimResponse, Record<string, any>>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.POST_CANCELLATION,
        method: 'POST',
        body: body,
        rawResponse: true,
      }),
    }),
    grievanceClaim: builder.mutation<RaiseClaimResponse, Record<string, any>>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.POST_GRIEVANCE,
        method: 'POST',
        body: body,
        rawResponse: true,
      }),
    }),
    surrenderClaim: builder.mutation<RaiseClaimResponse, Record<string, any>>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.POST_SURRENDER,
        method: 'POST',
        body: body,
        rawResponse: true,
      }),
    }),

    raiseEndorsement: builder.mutation<IRaiseClaim, IRaiseEndorsementRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.RAISE_ENDORSEMENT,
        method: 'POST',
        body: body,
      }),
    }),
    getFileDocuments: builder.query<
      GetFileDocumentResponse[],
      { insurance_category: string; service_request: string; service_request_type?: string }
    >({
      query: (params) => ({
        url: `${API_ENDPOINTS.BOB.GET_DOCUMENTS_FOR_FILE_UPLOAD}`,
        method: 'GET',
        params,
      }),
    }),
    getConsentData: builder.query<
      { consent_data: any; status?: string; consent_type?: string },
      any
    >({
      query: ({ consent_id }) => ({
        url: `${API_ENDPOINTS.BOB.GET_CONSENT}/?consent_id=${consent_id}`,
        method: 'GET',
        rawResponse: true,
      }),
      transformResponse: (response: { data: any }) => {
        return response.data
      },
    }),

    createConsent: builder.mutation<
      {
        consent_url: string
        consent_id: string
      },
      {
        pas_id: string
        email: string
        phone_number: string
        consent_data: any
        service_request?: string
        resend?: boolean
        consent_id?: string
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.CONSENT_CREATION,
        method: 'POST',
        body: body,
      }),
    }),
    sendOTP: builder.mutation<any, any>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SEND_OTP,
        method: 'POST',
        body: body,
      }),
    }),
    verifyOTP: builder.mutation<any, any>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.VERIFY_OTP_FOR_ENDORSEMENT,
        method: 'POST',
        body: body,
      }),
    }),
    submitConsentForm: builder.mutation<any, any>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SAVE_CONSENT,
        method: 'POST',
        body: body,
      }),
    }),
    allowedServices: builder.query<
      {
        Cancellation: boolean
        Claim: boolean
        Download_Payment_Receipts: boolean
        Download_Policy_Document: boolean
        Endorsement: boolean
        Grievance: boolean
        Renewal_Persistence_Payment: boolean
        Send_Payment_Renewal_Reminder: boolean
        Surrender: boolean
        Track_Request: boolean
        Renewal_Reminder: boolean
      }[],
      { pas_id: string }
    >({
      query: (params) => ({
        method: 'GET',
        url: API_ENDPOINTS.BOB.ALLOWED_SERVICES,
        params,
      }),
    }),
    sendReminder: builder.mutation<void, { pas_id: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SEND_REMINDER,
        method: 'POST',
        body: body,
      }),
    }),
    sendNonLifeReminder: builder.mutation<void, { pas_id: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.NON_LIFE_SEND_REMINDER,
        method: 'POST',
        body: body,
      }),
    }),
    getRenewalData: builder.query<
      {
        is_renewal_applicable: boolean
        renewal_completed: boolean
        renewal_details: {
          errFlag: string
          ConsentFlag: string
          ValidFlag: string
          ApplicationNumber: string
          PolicyStatus: string
          LGCode: string
          SPCode: string
          ProductCode: string
          ProductName: string
          PremiumAmount: string
          Salutation: string
          FirstName: string
          MiddleName: string
          LastName: string
          SumAssured: string
          PolicyTerm: string
          PremiumTerm: string
          PremiumFrequency: string
          PremiumType: string
          PremiumDueDate: string
          NextPremiumDueDate: string
          AdvancePremiumAmount: string
          Mobile: string
          EmailID: string
          SuspenseAmount: any
          data: any
        }
      },
      {
        pas_id: string
      }
    >({
      query: (params) => ({
        method: 'GET',
        url: API_ENDPOINTS.BOB.GET_RENEWAL_DATA,
        params,
      }),
    }),
    getRenewalDataMotor: builder.query<
      {
        is_renewal_applicable: boolean
        renewal_completed: boolean
        renewal_details: {
          errFlag: string
          ConsentFlag: string
          ValidFlag: string
          ApplicationNumber: string
          PolicyStatus: string
          LGCode: string
          SPCode: string
          ProductCode: string
          ProductName: string
          PremiumAmount: string
          Salutation: string
          FirstName: string
          MiddleName: string
          LastName: string
          SumAssured: string
          PolicyTerm: string
          PremiumTerm: string
          PremiumFrequency: string
          PremiumType: string
          PremiumDueDate: string
          NextPremiumDueDate: string
          AdvancePremiumAmount: string
          Mobile: string
          EmailID: string
          SuspenseAmount: any
        }
      },
      {
        pas_id: string
      }
    >({
      query: (params) => ({
        method: 'GET',
        url: API_ENDPOINTS.BOB.GET_RENEWAL_DATA_MOTOR,
        params,
      }),
    }),
    getRenewalDataHealth: builder.query<
      {
        // The base query unwraps the `data` envelope, so this is the inner object.
        premium: number
        totalPremium: number
        serviceTax: number
        period: number
        policyType: string
        policyTypeName: string
        proposerName: string
        proposerEmail: string
        proposerPhone: string
        proposerAddressOne: string
        proposerAddressTwo: string
        cityName: string
        stateName: string
        postalCode: string
        nomineeName: string
        nomineeRelationship: string
        nomineeAge: number
        referenceId: string
        EnhancedSumInsured: string
        esiSlabs: string[]
        stpEsiSlabs: string[]
        isEsiEligible: number
        esiQuestions: any[]
        insureds: any[]
        [key: string]: any
      },
      {
        pas_id: string
      }
    >({
      query: (params) => ({
        method: 'GET',
        url: API_ENDPOINTS.BOB.GET_RENEWAL_DATA_HEALTH,
        params,
      }),
    }),
    sendRenewalConsent: builder.mutation<
      {
        link: string
        consent_id: string
        tran_id: string
      },
      {
        pas_id: string
        stage: string
        product_journey_type: string
        customer_account_number: string
        amount: string
        consent_data?: any
        account_type: string
        is_resend?: boolean
        resend_consent_id?: string
        // Health renewal only - the declaration answer, folded into the consent token.
        isInGoodHealth?: number
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SEND_CONSENT_LINK,
        method: 'POST',
        body: body,
      }),
    }),
    checkConsentStatus: builder.query<
      {
        status: string
        comment: string
        retryable: boolean
        // Consent approval and the debit settle separately: `status` flips to APPROVED first
        // while `payment_status` is still Pending, and `retryable` stays true until both are
        // done. Optional so existing Customer 360 callers are unaffected.
        payment_status?: string
        /** Issued once the debit succeeds; the key for renewal-policy-status */
        token?: string
        data?: {
          token?: string
          /** 'completed' once the insurer has issued the renewed policy */
          renewal_status?: string
          renewal_policy_no?: string
          renewal_policy_pdf?: string
          amount?: number
          payment_mode?: string
          plan_name?: string
          [key: string]: unknown
        }
      },
      { consent_id: string; tran_id: string }
    >({
      query: (params) => ({
        method: 'GET',
        url: API_ENDPOINTS.BOB.CHECK_RENEWAL_LIFE_CONSENT_STATUS,
        params,
      }),
    }),
    getChoices: builder.query<
      {
        [key: string]: {
          code: string
          name: string
        }[]
      },
      { [key: string]: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_CHOICES,
        method: 'GET',
        params: params,
      }),
    }),
    getStandaloneRenewalProducts: builder.query<IRenewalProduct[], void>({
      query: () => ({
        url: API_ENDPOINTS.BOB.STANDALONE_RENEWAL_PRODUCTS,
        method: 'GET',
        // journeyType is 'Renewal' here, so the header is not derived — send it explicitly
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    // Returns a search_id immediately; the insurers are polled for separately
    startStandaloneRenewalSearch: builder.mutation<
      IStartRenewalSearchResponse,
      IStartRenewalSearchRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.STANDALONE_RENEWAL_SEARCH,
        method: 'POST',
        body: body,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    sendStandaloneRenewalConsent: builder.mutation<
      IStandaloneRenewalConsentResponse,
      IStandaloneRenewalConsentRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.STANDALONE_RENEWAL_CONSENT,
        method: 'POST',
        body: body,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    getRenewalPolicyStatus: builder.query<IRenewalPolicyStatus, { token: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.RENEWAL_POLICY_STATUS,
        method: 'GET',
        params: params,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    // NSP users need their SP to approve before the customer is asked.
    // rawResponse keeps the envelope: the backend can answer HTTP 200 with a non-200 status.
    sendStandaloneRenewalSpConsent: builder.mutation<
      IApiEnvelope<ISpConsentSendResponse>,
      ISpConsentSendRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.STANDALONE_RENEWAL_SP_CONSENT,
        method: 'POST',
        body: body,
        rawResponse: true,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    // Same call for an existing Customer 360 policy, keyed by pas_id
    sendRenewalSpConsent: builder.mutation<
      IApiEnvelope<ISpConsentSendResponse>,
      ISpConsentSendRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.RENEWAL_SP_CONSENT_SEND,
        method: 'POST',
        body: body,
        rawResponse: true,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    // Agent-side: watch the SP consent until they submit it
    getSpConsentStatus: builder.query<IApiEnvelope<ISpConsentStatus>, { consent_id: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.STANDALONE_RENEWAL_SP_STATUS,
        method: 'GET',
        params: params,
        rawResponse: true,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
    getStandaloneRenewalSearch: builder.query<IRenewalSearchResult, { search_id: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.STANDALONE_RENEWAL_SEARCH,
        method: 'GET',
        params: params,
        headers: { 'Journey-Type': 'Default' },
      }),
    }),
  }),
})

export const {
  useGetPolicyListingQuery,
  useLazyGetPolicyListingQuery,
  useGetLifeEndorseFormFieldsQuery,
  useRaiseClaimMutation,
  useGetTicketListingQuery,
  useCancellationClaimMutation,
  useGrievanceClaimMutation,
  useSurrenderClaimMutation,
  useRaiseEndorsementMutation,
  useLazyGetFileDocumentsQuery,
  useCreateConsentMutation,
  useLazyGetConsentDataQuery,
  useSendOTPMutation,
  useVerifyOTPMutation,
  useSubmitConsentFormMutation,
  useLazyAllowedServicesQuery,
  useSendReminderMutation,
  useLazyGetRenewalDataQuery,
  useLazyGetRenewalDataMotorQuery,
  useLazyGetRenewalDataHealthQuery,
  useSendRenewalConsentMutation,
  useLazyCheckConsentStatusQuery,
  useCheckConsentStatusQuery,
  useSendNonLifeReminderMutation,
  useLazyGetChoicesQuery,
  useGetStandaloneRenewalProductsQuery,
  useStartStandaloneRenewalSearchMutation,
  useGetStandaloneRenewalSearchQuery,
  useLazyGetStandaloneRenewalSearchQuery,
  useSendStandaloneRenewalConsentMutation,
  useSendStandaloneRenewalSpConsentMutation,
  useSendRenewalSpConsentMutation,
  useGetSpConsentStatusQuery,
  useGetRenewalPolicyStatusQuery,
} = customer360Api
